import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import '../styles/liveDemo.css';
import { initialContent, defaultSections } from '../data/liveDemoDefaults';
import { db } from '../firebase';

const fallbackTestimonials = [
    {
        name: 'Jordan Wells',
        title: 'Business traveler · Miami Airport',
        quote: 'Pickup took under 5 minutes and the SUV was spotless. Felt like a premium rental without the premium price.',
        image: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=240&q=80',
    },
    {
        name: 'Priya Desai',
        title: 'Family vacation · Orlando',
        quote: 'We landed at midnight and still checked out with zero lines. Support texted me the lockbox code before we landed.',
        image: 'https://images.unsplash.com/photo-1544723795-3fb6469f5b39?auto=format&fit=crop&w=240&q=80',
    },
    {
        name: 'Evan Brooks',
        title: 'Repeat renter · Tampa',
        quote: 'Extended my rental from my phone and kept the same rate. Fleet photos matched exactly what I picked up.',
        image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=240&q=80',
    },
];

const fallbackWhyIcons = ['🚗', '⚡', '🛡️'];

function LivePreview() {
    const { state, search } = useLocation();
    const previewId = new URLSearchParams(search).get('previewId');
    const [remoteContent, setRemoteContent] = useState(null);
    const [remoteSections, setRemoteSections] = useState(null);
    const [loading, setLoading] = useState(Boolean(previewId));
    const [loadError, setLoadError] = useState('');
    const content = remoteContent || state?.content || initialContent;
    const sections = remoteSections || state?.sections || defaultSections;
    const [navOpen, setNavOpen] = useState(false);
    const theme = content?.theme || initialContent.theme;
    const testimonials = content?.testimonials?.items?.length
        ? content.testimonials.items
        : fallbackTestimonials;
    const whyItems = (content?.why?.items || []).map((item, index) => ({
        ...item,
        icon: item.icon || fallbackWhyIcons[index % fallbackWhyIcons.length],
    }));
    const photoItems = content?.photos?.items?.length ? content.photos.items : [];
    const logoStyles = {
        width: content?.nav?.logoWidth ? `${content.nav.logoWidth}px` : undefined,
        height: content?.nav?.logoHeight ? `${content.nav.logoHeight}px` : 'auto',
    };
    const navAccent = content?.nav?.accent || '#6366f1';
    const navBackgroundMode = content?.nav?.backgroundMode || 'gradient';
    const navBackgroundColor = content?.nav?.backgroundColor || '#0b1022';
    const navGradientColor = content?.nav?.gradientColor || '#0b1022';
    const navLinkColors = Array.isArray(content?.nav?.linkColors) && content.nav.linkColors.length
        ? content.nav.linkColors
        : (content?.nav?.links || []).map(() => navAccent);
    const navCtaColor = content?.nav?.ctaColor || navAccent;
    const getNavLinkColor = (index) => navLinkColors[index] || navAccent;
    const navBackground = navBackgroundMode === 'solid'
        ? navBackgroundColor
        : `linear-gradient(90deg, ${navAccent}18, ${navGradientColor})`;
    const heroAccent = content?.hero?.accent || '#7c3aed';
    const heroPrimaryCtaColor = content?.hero?.ctaPrimaryColor || heroAccent;
    const heroSecondaryCtaColor = content?.hero?.ctaSecondaryColor || heroAccent;
    const heroBackgroundMediaType = content?.hero?.backgroundMediaType || 'video';
    const heroBackgroundMediaUrl = content?.hero?.backgroundMediaUrl
        || 'https://www.fiveguycarrentals.com/theme/img/videoplayback.mp4';
    const iconsAccent = content?.icons?.accent || '#7c3aed';
    const iconsBackgroundMode = content?.icons?.backgroundMode || 'gradient';
    const iconsBackgroundColor = content?.icons?.backgroundColor || '#0b1022';
    const iconsGradientColor = content?.icons?.gradientColor || '#0b1022';
    const iconsTextColor = content?.icons?.textColor || '#e2e8f0';
    const iconsIconColor = content?.icons?.iconColor || iconsAccent;
    const iconCtaColor = content?.icons?.ctaColor || iconsAccent;
    const iconSizeValue = Number(content?.icons?.iconSize);
    const iconSize = Number.isFinite(iconSizeValue) && iconSizeValue > 0 ? iconSizeValue : 52;
    const iconFontSize = Math.max(12, Math.round(iconSize * 0.45));
    const getIconColor = () => iconsIconColor;
    const iconsBackground = iconsBackgroundMode === 'solid'
        ? iconsBackgroundColor
        : `linear-gradient(135deg, ${iconsAccent}1f, ${iconsGradientColor})`;
    const adBannerAccent = content?.adBanner?.accent || theme?.accent || '#7c3aed';
    const adBannerBackgroundMode = content?.adBanner?.backgroundMode || 'gradient';
    const adBannerBackgroundColor = content?.adBanner?.backgroundColor || '#0b1022';
    const adBannerGradientColor = content?.adBanner?.gradientColor || '#0b1022';
    const adBannerTextColor = content?.adBanner?.textColor || '#e2e8f0';
    const adBannerBackground = adBannerBackgroundMode === 'solid'
        ? adBannerBackgroundColor
        : `linear-gradient(90deg, ${adBannerAccent}18, ${adBannerGradientColor})`;
    const testimonialsBackgroundMode = content?.testimonials?.backgroundMode || 'gradient';
    const testimonialsBackgroundColor = content?.testimonials?.backgroundColor || '#0b1022';
    const testimonialsGradientColor = content?.testimonials?.gradientColor || '#0b1022';
    const testimonialsTextColor = content?.testimonials?.textColor || '#e2e8f0';
    const testimonialsBackground = testimonialsBackgroundMode === 'solid'
        ? testimonialsBackgroundColor
        : `linear-gradient(135deg, ${content.testimonials.accent}16, ${testimonialsGradientColor})`;
    const whyBackgroundMode = content?.why?.backgroundMode || 'gradient';
    const whyBackgroundColor = content?.why?.backgroundColor || '#0b1022';
    const whyGradientColor = content?.why?.gradientColor || '#0b1022';
    const whyTextColor = content?.why?.textColor || '#e2e8f0';
    const whyBackground = whyBackgroundMode === 'solid'
        ? whyBackgroundColor
        : `linear-gradient(135deg, ${content.why.accent}16, ${whyGradientColor})`;
    const photosBackgroundMode = content?.photos?.backgroundMode || 'gradient';
    const photosBackgroundColor = content?.photos?.backgroundColor || '#0b1022';
    const photosGradientColor = content?.photos?.gradientColor || '#0b1022';
    const photosBackground = photosBackgroundMode === 'solid'
        ? photosBackgroundColor
        : `linear-gradient(135deg, ${content.photos.accent}16, ${photosGradientColor})`;
    const vehiclesBackgroundMode = content?.vehicles?.backgroundMode || 'gradient';
    const vehiclesBackgroundColor = content?.vehicles?.backgroundColor || '#0b1022';
    const vehiclesGradientColor = content?.vehicles?.gradientColor || '#0b1022';
    const vehiclesTextColor = content?.vehicles?.textColor || '#e2e8f0';
    const vehiclesCardBackgroundColor = content?.vehicles?.cardBackgroundColor || '#0f172a';
    const vehiclesBackground = vehiclesBackgroundMode === 'solid'
        ? vehiclesBackgroundColor
        : `linear-gradient(135deg, ${content.vehicles.accent}16, ${vehiclesGradientColor})`;
    const emailBackgroundMode = content?.email?.backgroundMode || 'gradient';
    const emailBackgroundColor = content?.email?.backgroundColor || '#0b1022';
    const emailGradientColor = content?.email?.gradientColor || '#0b1022';
    const emailTextColor = content?.email?.textColor || '#e2e8f0';
    const emailBackground = emailBackgroundMode === 'solid'
        ? emailBackgroundColor
        : `linear-gradient(135deg, ${content.email.accent}16, ${emailGradientColor})`;
    const footerBackgroundMode = content?.footer?.backgroundMode || 'gradient';
    const footerBackgroundColor = content?.footer?.backgroundColor || '#0b1022';
    const footerGradientColor = content?.footer?.gradientColor || '#0b1022';
    const footerTextColor = content?.footer?.textColor || '#e2e8f0';
    const footerBackground = footerBackgroundMode === 'solid'
        ? footerBackgroundColor
        : `linear-gradient(135deg, ${content.footer.accent}16, ${footerGradientColor})`;
    const formatVehiclePrice = (price, rate) => {
        if (price === '' || price === null || typeof price === 'undefined') {
            return '';
        }
        const numericPrice = typeof price === 'number'
            ? price
            : Number(String(price).replace(/[^0-9.]/g, ''));
        if (!Number.isFinite(numericPrice)) {
            return '';
        }
        const cleanedRate = String(rate || 'Day').replace(/^\//, '') || 'Day';
        return `$${numericPrice}/${cleanedRate}`;
    };

    useEffect(() => {
        if (!previewId) {
            setLoading(false);
            return;
        }

        let isMounted = true;
        const loadPreview = async () => {
            try {
                const previewRef = doc(db, 'previews', previewId);
                const snapshot = await getDoc(previewRef);
                if (!snapshot.exists()) {
                    throw new Error('Preview not found');
                }
                const data = snapshot.data();
                if (isMounted) {
                    setRemoteContent(data.content || initialContent);
                    setRemoteSections(data.sections || defaultSections);
                    setLoadError('');
                }
            } catch (error) {
                console.error('Failed to load preview', error);
                if (isMounted) {
                    setLoadError('Preview not found or unavailable.');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadPreview();

        return () => {
            isMounted = false;
        };
    }, [previewId]);

    if (previewId && loading) {
        return (
            <div className="live-demo-page live-preview-page">
                <section className="live-demo-workbench">
                    <div className="container">
                        <div className="preview-panel preview-panel--preview">
                            <div className="preview-body">
                                <div className="preview-card">
                                    Loading preview...
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div
            className="live-demo-page live-preview-page controls-modern"
            style={{
                background: theme.background,
                color: theme.text || '#e2e8f0',
            }}
        >
            <section className="live-demo-workbench">
                <div className="container">
                    <div className="preview-panel preview-panel--preview">
                        {loadError && (
                            <div className="preview-body">
                                <div className="preview-banner" role="alert">
                                    {loadError}
                                </div>
                            </div>
                        )}
                        {sections.adBanner && (
                            <div>
                                <div
                                className="ad-banner"
                                    style={{
                                        background: adBannerBackground,
                                        border: `1px solid ${adBannerAccent}30`,
                                        color: adBannerTextColor,
                                        fontSize: '0.95rem',
                                    }}
                                >
                                    <div className="ad-banner__phone" style={{ fontWeight: 700 }}>
                                        {content.adBanner.phone || '(833) 353-3847'}
                                    </div>
                                    <div className="ad-banner__quote" style={{ lineHeight: 1.4 }}>
                                        "{content.adBanner.quote}" — <span style={{ opacity: 0.85 }}>{content.adBanner.author}</span>
                                    </div>
                                    <div className="ad-banner__email" style={{ fontWeight: 600 }}>
                                        support@fleetifysoftware.com
                                    </div>
                                </div>
                            </div>
                        )}
                        {sections.nav && (
                            <div className="preview-nav-card">
                                <div
                                    className="preview-nav"
                                    style={{
                                        background: navBackground,
                                    }}
                                >
                                    <div className="preview-nav-left">
                                        {content.nav?.logo ? (
                                            <img
                                                className="preview-logo-image"
                                                src={content.nav.logo}
                                                alt={content.nav?.brand || 'Logo'}
                                                style={logoStyles}
                                            />
                                        ) : (
                                            <div
                                                className="preview-logo"
                                                style={{ background: navAccent, color: '#ffffff' }}
                                            >
                                                Your Logo
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="preview-hamburger"
                                            aria-label="Toggle navigation menu"
                                            aria-expanded={navOpen}
                                            onClick={() => setNavOpen((prev) => !prev)}
                                        >
                                            ≡
                                        </button>
                                    </div>
                                    <div className={`preview-nav-menu ${navOpen ? 'open' : ''}`}>
                                        <div className="preview-menu">
                                            {(content.nav?.links || []).map((link, index) => {
                                                const linkColor = getNavLinkColor(index);
                                                const isActive = index === 0;
                                                return (
                                                    <span
                                                        key={link + index}
                                                        className={`preview-link ${isActive ? 'active' : ''}`}
                                                        style={{
                                                            borderColor: `${linkColor}55`,
                                                            background: isActive ? `${linkColor}33` : `${linkColor}1a`,
                                                            color: isActive ? '#fff' : '#e2e8f0',
                                                        }}
                                                    >
                                                        {link}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="preview-nav-actions">
                                        <span
                                            className="btn btn-primary btn-tiny"
                                            style={{
                                                background: navCtaColor,
                                                borderColor: navCtaColor,
                                            }}
                                        >
                                            {content.nav?.cta || 'Book now'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="preview-body">
                        {sections.hero && (
                            <div
                                className="preview-hero-card"
                                style={{ padding: '28px', minHeight: '820px', display: 'grid', gap: '18px' }}
                            >
                                {heroBackgroundMediaType !== 'none' && heroBackgroundMediaUrl && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            zIndex: 0,
                                            overflow: 'hidden',
                                            borderRadius: 'inherit',
                                        }}
                                        aria-hidden="true"
                                    >
                                        {heroBackgroundMediaType === 'video' ? (
                                            <video
                                                src={heroBackgroundMediaUrl}
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    filter: 'brightness(0.65)',
                                                }}
                                            />
                                        ) : (
                                            <img
                                                src={heroBackgroundMediaUrl}
                                                alt=""
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    filter: 'brightness(0.65)',
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                                <div className="hero-head" style={{ gap: '14px' }}>
                                    <div>
                                        <h3 style={{ fontSize: '2.4rem', marginBottom: '6px' }}>{content.hero.headline}</h3>
                                        <p className="preview-lead" style={{ fontSize: '1.05rem', lineHeight: 1.6 }}>{content.hero.subhead}</p>
                                    </div>
                                    <div className="hero-chip-stack">
                                        <span
                                            className="chip hero-chip"
                                            style={{ borderColor: content.hero.accent, color: content.hero.accent }}
                                        >
                                            Premium fleet
                                        </span>
                                        <span className="chip ghost">4.9 Rated</span>
                                    </div>
                                </div>

                                <div className="hero-form-shell" style={{ padding: '18px' }}>
                                    <div className="hero-form-header" style={{ marginBottom: '12px' }}>
                                        <div>
                                <p className="card-eyebrow">Trip details</p>
                                            <p className="helper-text" style={{ fontSize: '0.98rem' }}>Enter dates and locations to see live availability.</p>
                                        </div>
                                        <span className="chip ghost">Instant pickup</span>
                                    </div>

                                    <div className="hero-form">
                                        <div className="hero-form-grid" style={{ gap: '12px' }}>
                                            <div className="hero-field">
                                                <label>Pick-up Date</label>
                                                <input type="text" value="12/11/2025" readOnly />
                                            </div>
                                            <div className="hero-field">
                                                <label>Pick-up Time</label>
                                                <input type="text" value="10:00 AM" readOnly />
                                            </div>
                                            <div className="hero-field">
                                                <label>Return Date</label>
                                                <input type="text" value="--/--/----" readOnly />
                                            </div>
                                            <div className="hero-field">
                                                <label>Return Time</label>
                                                <input type="text" value="10:00 AM" readOnly />
                                            </div>
                                        </div>

                                        <div className="hero-locations-grid" style={{ gap: '12px' }}>
                                            <div className="hero-field">
                                                <label>Pick-up location</label>
                                                <div className="hero-location">
                                                    <input type="text" value="Hogarth Road, London" readOnly />
                                                </div>
                                            </div>

                                            <div className="hero-field">
                                                <label>Return location</label>
                                                <div className="hero-location">
                                                    <input type="text" value="Hogarth Road, London" readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="preview-actions hero-actions-row" style={{ gap: '12px' }}>
                                        <span
                                            className="btn btn-primary btn-small"
                                            style={{
                                                background: heroPrimaryCtaColor,
                                                borderColor: heroPrimaryCtaColor,
                                                padding: '10px 18px',
                                                fontSize: '0.98rem',
                                                borderRadius: '9999px',
                                            }}
                                        >
                                            {content.hero.ctaPrimary}
                                        </span>
                                        <span
                                            className="btn btn-outline btn-small"
                                            style={{
                                                borderColor: heroSecondaryCtaColor,
                                                color: heroSecondaryCtaColor,
                                                padding: '10px 18px',
                                                fontSize: '0.98rem',
                                            }}
                                        >
                                            {content.hero.ctaSecondary}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {sections.icons && (
                            <div
                                className="icons-block"
                                style={{
                                    padding: '20px 18px',
                                    gap: '14px',
                                    background: iconsBackground,
                                }}
                            >
                                <div className="icon-grid" style={{ gap: '14px' }}>
                                    {content.icons.items.map((item, index) => (
                                        <div key={index} className="icon-item">
                                            <div
                                                className="icon-figure"
                                                style={{
                                                    background: `${getIconColor(index)}26`,
                                                    borderColor: `${getIconColor(index)}50`,
                                                    color: getIconColor(index),
                                                    width: `${iconSize}px`,
                                                    height: `${iconSize}px`,
                                                    fontSize: `${iconFontSize}px`,
                                                }}
                                            >
                                                {item.icon || index + 1}
                                            </div>
                                            <p className="icon-title" style={{ fontSize: '1.05rem', color: iconsTextColor }}>
                                                {item.title}
                                            </p>
                                            <p className="icon-copy" style={{ fontSize: '0.98rem', color: iconsTextColor }}>
                                                {item.description}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                                <div className="icon-section-footer">
                                    <p className="icon-section-title" style={{ fontSize: '1.2rem', color: iconsTextColor }}>
                                        {content.icons.title}
                                    </p>
                                    <span
                                        className="btn btn-primary btn-small"
                                        style={{
                                            background: iconCtaColor,
                                            borderColor: iconCtaColor,
                                            padding: '9px 16px',
                                            fontSize: '0.95rem',
                                        }}
                                    >
                                        {content.icons.ctaLabel}
                                    </span>
                                </div>
                            </div>
                        )}

                        {sections.vehicles && (
                            <div className="preview-card wide">
                                <div
                                    style={{
                                        display: 'grid',
                                        gap: '22px',
                                        padding: '28px',
                                        borderRadius: '18px',
                                        background: vehiclesBackground,
                                        border: `1px solid ${content.vehicles.accent}2e`,
                                        boxShadow: '0 12px 35px rgba(0, 0, 0, 0.35)',
                                        color: vehiclesTextColor,
                                        width: '100%',
                                        maxWidth: '1240px',
                                        margin: '0 auto',
                                    }}
                                >
                                    <div style={{ display: 'grid', gap: '10px', textAlign: 'center' }}>
                                        <h3 style={{ margin: 0, fontSize: '2.6rem', color: vehiclesTextColor }}>
                                            {content.vehicles.heading || 'Find Your Drive'}
                                        </h3>
                                        <p
                                            className="helper-text"
                                            style={{
                                                margin: 0,
                                                maxWidth: '880px',
                                                justifySelf: 'center',
                                                fontSize: '1rem',
                                                color: vehiclesTextColor,
                                            }}
                                        >
                                            {content.vehicles.subhead || ''}
                                        </p>
                                    </div>

                                    <div
                                        className="vehicle-grid"
                                    >
                                        {content.vehicles.items
                                            .filter((item) => item.showOnHomepage !== false)
                                            .map((item, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    position: 'relative',
                                                    background: item.backgroundColor || vehiclesCardBackgroundColor,
                                                    border: `1px solid ${content.vehicles.accent}24`,
                                                    borderRadius: '14px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
                                                    display: 'grid',
                                                    gridTemplateRows: '200px auto auto',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        position: 'relative',
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        backgroundImage: item.image
                                                            ? `url(${item.image})`
                                                            : `linear-gradient(135deg, ${content.vehicles.accent}26, ${content.vehicles.accent}12)`,
                                                    }}
                                                    role="img"
                                                    aria-label={item.title}
                                                >
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            background:
                                                                'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.55) 100%)',
                                                        }}
                                                    ></div>
                                                </div>
                                                <div style={{ padding: '16px 16px 10px', display: 'grid', gap: '8px' }}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: '8px',
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        <strong style={{ fontSize: '1.05rem', color: vehiclesTextColor }}>{item.title}</strong>
                                                        {formatVehiclePrice(item.price, item.rate) && (
                                                            <span
                                                                style={{
                                                                    color: content.vehicles.accent,
                                                                    fontWeight: 700,
                                                                    display: 'flex',
                                                                    alignItems: 'baseline',
                                                                    gap: '4px',
                                                                }}
                                                            >
                                                                <span style={{ fontSize: '1.25rem' }}>
                                                                    {formatVehiclePrice(item.price, item.rate)}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    {item.description && (
                                                        <p style={{ margin: 0, color: vehiclesTextColor, fontSize: '0.98rem' }}>
                                                            {item.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '8px',
                                                        padding: '0 14px 12px',
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-small"
                                                        style={{
                                                            background: content.vehicles.accent,
                                                            borderColor: content.vehicles.accent,
                                                            borderRadius: '9999px',
                                                            padding: '9px 16px',
                                                            fontSize: '0.95rem',
                                                        }}
                                                    >
                                                        Rent Me
                                                    </button>
                                                    <span style={{ color: content.vehicles.accent, fontSize: '0.95rem' }}>
                                                        {item.note || content.vehicles.badge}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        {sections.testimonials && (
                            <div
                                className="preview-card wide"
                                style={{
                                    background: testimonialsBackground,
                                    border: `1px solid ${content.testimonials.accent}33`,
                                    padding: '64px 22px',
                                    minHeight: '720px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    color: testimonialsTextColor,
                                }}
                            >
                                <div style={{ display: 'grid', gap: '18px' }}>
                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                            <span
                                                className="chip"
                                                style={{
                                                    borderColor: content.testimonials.accent,
                                                    color: content.testimonials.accent,
                                                }}
                                            >
                                                {content.testimonials.badge}
                                            </span>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    color: content.testimonials.accent,
                                                }}
                                            >
                                                {[...Array(5)].map((_, starIndex) => (
                                                    <span key={starIndex} aria-hidden="true">
                                                        ★
                                                    </span>
                                                ))}
                                                <span className="helper-text" style={{ color: testimonialsTextColor }}>
                                                    5.0 average rating
                                                </span>
                                            </div>
                                        </div>
                                        <h3 style={{ margin: 0, fontSize: '1.9rem', color: testimonialsTextColor }}>
                                            Thousands of Happy Customers
                                        </h3>
                                        <p className="helper-text" style={{ margin: 0, color: testimonialsTextColor }}>
                                            {content.testimonials.quote}
                                        </p>
                                    </div>

                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                                            gap: '14px',
                                        }}
                                    >
                                        {testimonials.map((item, index) => (
                                            <div
                                                key={item.name + index}
                                                style={{
                                                    background: 'rgba(255,255,255,0.02)',
                                                    border: `1px solid ${content.testimonials.accent}26`,
                                                    borderRadius: '14px',
                                                    padding: '14px 14px 16px',
                                                    display: 'grid',
                                                    gap: '10px',
                                                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div
                                                        style={{
                                                            width: '52px',
                                                            height: '52px',
                                                            borderRadius: '50%',
                                                            overflow: 'hidden',
                                                            border: `2px solid ${content.testimonials.accent}`,
                                                            background: `${content.testimonials.accent}12`,
                                                        }}
                                                        aria-hidden="true"
                                                    >
                                                        <img
                                                            src={item.image}
                                                            alt={item.name}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    </div>
                                                    <div style={{ display: 'grid', gap: '4px' }}>
                                                        <strong style={{ color: testimonialsTextColor }}>{item.name}</strong>
                                                        <span className="helper-text" style={{ color: testimonialsTextColor }}>
                                                            {item.title}
                                                        </span>
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                color: content.testimonials.accent,
                                                            }}
                                                            aria-label="Rated 5 out of 5"
                                                        >
                                                            {[...Array(5)].map((_, starIndex) => (
                                                                <span key={starIndex} aria-hidden="true">
                                                                    ★
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                <p style={{ margin: 0, color: testimonialsTextColor, lineHeight: 1.55 }}>
                                                    {item.quote}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {sections.why && (
                            <div
                                className="preview-card wide why-section"
                                style={{
                                    background: whyBackground,
                                    color: whyTextColor,
                                    padding: '72px 0',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'grid',
                                        gap: '14px',
                                        padding: '18px',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                        <div style={{ display: 'grid', gap: '4px' }}>
                                            <p className="card-eyebrow" style={{ margin: 0 }}>Why Rent With Us</p>
                                            <h3 style={{ margin: 0, color: whyTextColor }}>Built for fast, reliable rentals</h3>
                                            <p className="helper-text" style={{ margin: 0, color: whyTextColor }}>
                                                Instant pickups, verified insurance, and transparent pricing with no surprises.
                                            </p>
                                        </div>
                                        <span
                                            className="chip"
                                            style={{
                                                borderColor: content.why.accent,
                                                color: content.why.accent,
                                            }}
                                        >
                                            4.9 ★ rated
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                            gap: '14px',
                                        }}
                                    >
                                        {whyItems.map((item, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    background: 'rgba(255,255,255,0.02)',
                                                    border: `1px solid ${content.why.accent}24`,
                                                    borderRadius: '12px',
                                                    padding: '14px',
                                                    display: 'grid',
                                                    gap: '10px',
                                                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.16)',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '44px',
                                                        height: '44px',
                                                        borderRadius: '12px',
                                                        background: `${content.why.accent}22`,
                                                        border: `1px solid ${content.why.accent}55`,
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        fontSize: '1.2rem',
                                                    }}
                                                    aria-hidden="true"
                                                >
                                                    {item.icon || '✓'}
                                                </div>
                                                <div style={{ display: 'grid', gap: '6px' }}>
                                                    <strong style={{ color: whyTextColor }}>{item.title}</strong>
                                                    <p style={{ margin: 0, color: whyTextColor, lineHeight: 1.5 }}>{item.description}</p>
                                                </div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        fontSize: '0.92rem',
                                                        color: whyTextColor,
                                                    }}
                                                >
                                                    <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: content.why.accent }}></span>
                                                    <span>Instant booking ready</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        {sections.photos && (
                            <div
                                className="preview-card wide"
                                style={{
                                    background: photosBackground,
                                    padding: '24px',
                                }}
                            >
                                <div className="photo-grid">
                                    {photoItems.map((item, index) => (
                                        <div
                                            key={index}
                                            className="photo-item"
                                            style={{
                                                borderColor: `${content.photos.accent}40`,
                                                background: item.image
                                                    ? `linear-gradient(180deg, rgba(15, 23, 42, 0.2), rgba(15, 23, 42, 0.75)), url(${item.image})`
                                                    : `linear-gradient(135deg, ${content.photos.accent}33, ${content.photos.accent}18)`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                            }}
                                        >
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {sections.email && (
                            <div
                                className="preview-card wide"
                                style={{
                                    background: emailBackground,
                                    color: emailTextColor,
                                    padding: '24px',
                                }}
                            >
                                <div className="email-intro">
                                    <h3>Want 10% off your first rental with us?</h3>
                                    <p className="helper-text">Subscribe to our email list & receive 10% off today</p>
                                </div>
                                <div className="email-row">
                                    <input type="email" placeholder={content.email.placeholder} readOnly />
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-small"
                                        style={{
                                            background: content.email.accent,
                                            borderColor: content.email.accent,
                                        }}
                                    >
                                        {content.email.buttonText}
                                    </button>
                                </div>
                            </div>
                        )}

                        {sections.footer && (
                            <div
                                className="preview-card wide footer-section"
                                style={{
                                    background: footerBackground,
                                    color: footerTextColor,
                                    padding: '32px 22px',
                                }}
                            >
                                <div className="footer-grid">
                                    <div className="footer-brand">
                                        {content.nav?.logo && (
                                            <img
                                                className="footer-logo"
                                                src={content.nav.logo}
                                                alt={content.nav?.brand || 'Logo'}
                                                style={logoStyles}
                                            />
                                        )}
                                        {!content.nav?.logo && (
                                            <strong className="footer-title">{content.footer.title}</strong>
                                        )}
                                        <p className="helper-text">{content.footer.tagline}</p>
                                        <div className="footer-badge">{content.footer.badgeText}</div>
                                    </div>
                                    <div className="footer-links">
                                        <div className="footer-column">
                                            <p className="footer-heading">Links</p>
                                            <div className="footer-link-row">
                                                <a className="footer-link" href="/privacy-policy">Privacy Policy</a>
                                                <a className="footer-link" href="/terms-of-service">Terms of Service</a>
                                                <a className="footer-link" href="/car-rental-agreement">Car Rental Agreement</a>
                                            </div>
                                            <p className="footer-heading footer-heading-spaced">{content.footer.contactTitle}</p>
                                            <a className="footer-link" href={`mailto:${content.footer.contactEmail}`}>
                                                {content.footer.contactEmail}
                                            </a>
                                            <span className="footer-link">{content.footer.contactPhone}</span>
                                        </div>
                                        <div className="footer-column">
                                            <p className="footer-heading">{content.footer.hoursTitle}</p>
                                            <span className="footer-link">{content.footer.hoursDays}</span>
                                            <span className="footer-link">{content.footer.hoursTime}</span>
                                            <p className="footer-heading footer-heading-spaced">{content.footer.followTitle}</p>
                                            <div className="footer-social">
                                                <a
                                                    className="footer-link footer-social-link"
                                                    href={content.footer.instagramUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <span className="footer-social-icon" aria-hidden="true">
                                                        <svg viewBox="0 0 24 24" role="presentation">
                                                            <path
                                                                d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm10 2H7a2 2 0 0 0-2 2v10c0 1.1.9 2 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm-5 3.2A3.8 3.8 0 1 1 8.2 12 3.81 3.81 0 0 1 12 8.2zm0 2A1.8 1.8 0 1 0 13.8 12 1.8 1.8 0 0 0 12 10.2zm4.3-2.35a1 1 0 1 1-1-1 1 1 0 0 1 1 1z"
                                                                fill="currentColor"
                                                            />
                                                        </svg>
                                                    </span>
                                                    {content.footer.instagramLabel}
                                                </a>
                                                <a
                                                    className="footer-link footer-social-link"
                                                    href={content.footer.facebookUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <span className="footer-social-icon" aria-hidden="true">
                                                        <svg viewBox="0 0 24 24" role="presentation">
                                                            <path
                                                                d="M13.5 8.5V7.2c0-.7.4-1.1 1.2-1.1h1.3V4h-2.2C11.6 4 10.5 5.2 10.5 7.1v1.4H9v2.7h1.5V20h3V11.2h2.2l.4-2.7h-2.6z"
                                                                fill="currentColor"
                                                            />
                                                        </svg>
                                                    </span>
                                                    {content.footer.facebookLabel}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="footer-bottom">
                                    <span className="footer-note">© 2025 {content.footer.title}. All rights reserved.</span>
                                    <div className="footer-legal">
                                        <span className="footer-link">Privacy</span>
                                        <span className="footer-link">Terms</span>
                                        <span className="footer-link">Security</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default LivePreview;
